/**
 * JobWorkspacePage — one job's whole lifecycle on one page, keyed on the
 * Tharstern job number. Route: `/jobs/:jobNo`.
 *
 * Redesigned per docs/features/job-workspace-redesign/01_product_brief.md:
 * a single page (no tabs) — header, a real Tharstern stage rail, a
 * left-hand section nav, and anchored sections. The sections follow the
 * one-truth rule (docs/features/job-workspace-redesign/03_journey_glossary_and_ia.md
 * §1a): every fact renders in exactly one place, with its source and time —
 * Order (what was ordered and paid), Delivery (where it is going), Artwork
 * & proof (what prepress holds), Production, Invoice, Timeline — and the
 * Checks list at the end answers "what does a human still have to decide?"
 * by pointing at those sections rather than restating them. The stage
 * rail and the merged timeline are driven by fields `GET /views/job-progress`
 * already returned and this page used to discard (`stepper`, `timeline`) —
 * see the brief's "the thread and the breadcrumb already exist and are
 * discarded". `computeLifecycleStage` and its hand-rolled five-stage spine
 * are deleted, not kept as a fallback: the backend's `stepper` is the real
 * per-job stage rail, driven by Tharstern's own milestone groups.
 *
 * Booking-on through CIS, its Checks list and the Artwork & proof section it
 * fed retired with booking_module (order to invoice step 3b, doc 48 §2): a
 * job's prepress record returns with the artwork family. The page shows a
 * job our replica holds; any other job number is not found.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileText,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type * as API from "@shared-core/data/api-types";
import type {
  TrackingEmptyReason,
  TrackingResult,
} from "@shared-core/data/api/tracking-types";
import type {
  JobEmailLink,
  JobWorkspaceView,
  MeetingLink,
} from "@shared-core/data/adapter";
import type { JobInvoiceLine } from "@shared-core/data/job-invoice-types";
import { paths } from "@shared-core/routing/routes";
import {
  formatCurrencyGbp,
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { useSmartBack } from "../app/useSmartBack";
import { Card, CardContent } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { CollapsibleSection } from "../app/components/patterns/CollapsibleSection";
import { cn } from "../app/components/primitives/utils";
import { useCanUseTharsternAttachments } from "../app/auth/hooks";
import { JobTimelineSection } from "../app/components/domain/work-units/JobTimelineSection";
import type { ReplyComposerJobContext } from "../app/components/domain/csr-command-centre/ReplyComposer";
import { JobDeliveryBody } from "../app/components/domain/work-units/JobDeliveryBody";
import { Stepper } from "../app/components/patterns/Stepper";
import {
  stageSteps,
  StagePanel,
  DeliveryPanel,
} from "../app/components/domain/work-units/WorkStagePanels";

// ---------------------------------------------------------------------------
// Section identity
// ---------------------------------------------------------------------------

const SECTION_IDS = [
  "job-detail",
  "order",
  "delivery",
  "production",
  "invoice",
  "timeline",
] as const;
type SectionId = (typeof SECTION_IDS)[number];

const SECTION_LABELS: Record<SectionId, string> = {
  "job-detail": "Job detail",
  order: "Order",
  delivery: "Delivery",
  production: "Production",
  invoice: "Invoice",
  timeline: "Timeline",
};

// Sections that start expanded — everything else starts collapsed with a
// one-line summary, per the journey-iteration decision recorded in the brief.
const DEFAULT_OPEN: Record<SectionId, boolean> = {
  "job-detail": true,
  order: true,
  delivery: true,
  production: false,
  invoice: false,
  timeline: true,
};

// Which section a stage-rail stop scrolls to. Pre-press/materials/
// production/finishing are all press-floor stages the Production section
// covers; "Ready to despatch" is the one stop that concerns Delivery.
const STAGE_CODE_TO_SECTION: Record<string, SectionId> = {
  pre_press: "production",
  materials: "production",
  production: "production",
  finishing: "production",
  despatch: "delivery",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function JobWorkspacePage() {
  const { jobNo: rawJobNo } = useParams<{ jobNo: string }>();
  const jobNo = rawJobNo ? decodeURIComponent(rawJobNo) : "";
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const goBack = useSmartBack(paths.jobs());

  const canUseTharsternAttachments = useCanUseTharsternAttachments();

  const [loadingJob, setLoadingJob] = useState(true);
  const [job, setJob] = useState<API.JobViewItem | null>(null);
  // The composed read (spec 08): progress, planned delivery, invoice and
  // the estimate brief all live inside this one object now — see
  // `JobWorkspaceView`. Replaces what used to be four separate calls
  // (getJobProgress / getJobDelivery / getJobInvoice / getEstimateById).
  const [job360, setJob360] = useState<JobWorkspaceView | null>(null);
  const [tracking, setTracking] = useState<TrackingResult[]>([]);
  const [trackingEmptyReason, setTrackingEmptyReason] =
    useState<TrackingEmptyReason | null>(null);
  const [trackingUntrackedCourier, setTrackingUntrackedCourier] = useState<
    string | null
  >(null);
  const [trackingLoading, setTrackingLoading] = useState(true);
  // Distinguishes "the by-job tracking request itself failed" (network/5xx —
  // genuinely broken) from "it succeeded and came back with an empty_reason"
  // (a courier/Tharstern-level outcome the backend already diagnosed). Only
  // the former sets this; the .catch(() => []) that used to swallow both
  // into one blank panel is exactly the bug this fixes.
  const [trackingFailed, setTrackingFailed] = useState(false);
  const [emails, setEmails] = useState<JobEmailLink[]>([]);
  const [meetings, setMeetings] = useState<MeetingLink[]>([]);
  const [commsFailed, setCommsFailed] = useState(false);
  // Live Tharstern read, for the sales rep / CSR slots the replica does not
  // sync. Tracked separately from `job` so Job detail can tell "no
  // reference recorded" apart from "couldn't reach Tharstern" — rendering
  // both as a blank would turn an outage into a false "unassigned" signal.
  const [tharsternJob, setTharsternJob] = useState<API.TharsternJob | null>(
    null,
  );
  const [tharsternRefsFailed, setTharsternRefsFailed] = useState(false);
  const [activeStageKey, setActiveStageKey] = useState<string | null>(null);

  const [openSections, setOpenSections] =
    useState<Record<SectionId, boolean>>(DEFAULT_OPEN);
  const [activeSection, setActiveSection] = useState<SectionId>("job-detail");
  const sectionRefs = useRef<Record<SectionId, HTMLDivElement | null>>(
    {} as Record<SectionId, HTMLDivElement | null>,
  );

  const progress = job360?.progress ?? null;
  const jobDelivery = job360?.plannedDelivery ?? null;
  const invoiceLine: JobInvoiceLine | null = job360?.invoice ?? null;
  const estimateBrief = job360?.estimateBrief ?? null;
  const degradedSlices = job360?.degradedSlices ?? [];

  useEffect(() => {
    if (!jobNo) return;
    let cancelled = false;
    async function load() {
      setLoadingJob(true);
      setTrackingLoading(true);
      try {
        const [jobResult, job360Result, trackingResult] = await Promise.all([
          adapter.getChargeableJobById(jobNo).catch(() => null),
          adapter.getJobWorkspace(jobNo).catch(() => null),
          // `null` here means the request itself failed (network/5xx) —
          // distinct from a successful response whose `items` is empty
          // for a diagnosed reason (see `trackingEmptyReason` below).
          adapter.getJobTracking(jobNo).catch(() => null),
        ]);
        if (cancelled) return;
        setJob(jobResult);
        setJob360(job360Result);
        if (trackingResult) {
          setTracking(trackingResult.items);
          setTrackingEmptyReason(trackingResult.empty_reason ?? null);
          setTrackingUntrackedCourier(trackingResult.untracked_courier ?? null);
          setTrackingFailed(false);
        } else {
          setTracking([]);
          setTrackingEmptyReason(null);
          setTrackingUntrackedCourier(null);
          setTrackingFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoadingJob(false);
          setTrackingLoading(false);
        }
      }
    }
    async function loadTharsternRefs() {
      setTharsternRefsFailed(false);
      try {
        const live = await adapter.getTharsternJobByNo(jobNo);
        if (!cancelled) setTharsternJob(live);
      } catch {
        if (!cancelled) setTharsternRefsFailed(true);
      }
    }
    async function loadComms() {
      setCommsFailed(false);
      try {
        const comms = await adapter.getJobComms(jobNo);
        if (cancelled) return;
        setEmails(comms.emails);
        setMeetings(comms.meetings);
      } catch {
        if (!cancelled) setCommsFailed(true);
      }
    }
    void load();
    void loadComms();
    void loadTharsternRefs();
    return () => {
      cancelled = true;
    };
  }, [adapter, jobNo]);

  // Auto-pick the active production stage: prefer in-progress, else the
  // last complete one, else the first.
  useEffect(() => {
    if (activeStageKey !== null) return;
    const stages = progress?.stages ?? [];
    if (stages.length === 0) return;
    const inProgress = stages.find((p) => p.state === "in_progress");
    if (inProgress) {
      setActiveStageKey(inProgress.op_type);
      return;
    }
    const lastComplete = [...stages]
      .reverse()
      .find((p) => p.state === "complete");
    setActiveStageKey((lastComplete ?? stages[0]).op_type);
  }, [progress, activeStageKey]);

  // Scroll-spy: whichever section has most recently scrolled past a
  // reference line just below the sticky header is "active" for the left
  // rail's highlight. Guarded — jsdom/happy-dom (unit tests, Storybook's
  // test runner) don't implement IntersectionObserver, and losing the
  // highlight there is harmless; losing the page is not.
  //
  // Two independent problems here, both real:
  //
  // 1. `recompute()` never decides from the entries the observer callback
  //    hands it: a callback fires only for targets whose intersection
  //    status *changed*, so during a real scroll it is common for two or
  //    more sections to be simultaneously intersecting while only one of
  //    them appears in a given callback's `entries`. Picking from that
  //    partial batch silently sticks on whichever section last happened
  //    to change, however far it has since scrolled past. So the observer
  //    is only ever a signal to recompute: on every firing, every
  //    section's live position is read fresh and the one with the
  //    largest top at-or-above the reference line wins — the standard
  //    scroll-spy rule, computed from complete, current state.
  //
  // 2. That still needs the observer to actually *fire* at the right
  //    times. `{ threshold: 0 }` with no `rootMargin` fires only on a
  //    target crossing the real viewport's top/bottom edge. Most of a
  //    scroll through a tall section (Timeline, here) is a dead zone:
  //    every section above it already exited the viewport and every
  //    section below hasn't entered yet, so nothing crosses a viewport
  //    edge and the observer never fires — the highlight sticks on
  //    whatever it last was, even though `recompute()` itself would
  //    return the right answer if it ran. The fix is a reference *band*
  //    instead of the whole viewport: a thin `rootMargin`-shrunk strip
  //    just below the header, so a section entering/leaving that strip
  //    (not the viewport) is what fires the callback. Viewport height
  //    comes from the `getViewportSize` capability port, never
  //    `window.innerHeight` directly — pages don't touch browser globals.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const REFERENCE_LINE_PX = 110; // just below the sticky header/stage rail
    const BAND_HEIGHT_PX = 10; // thin — just enough to guarantee a crossing

    const recompute = () => {
      let bestId: SectionId | null = null;
      let bestTop = -Infinity;
      for (const id of SECTION_IDS) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= REFERENCE_LINE_PX && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }
      // Above every section (scrolled to the very top) — default to the
      // first one that exists rather than leaving the highlight stale.
      if (!bestId) {
        bestId = SECTION_IDS.find((id) => sectionRefs.current[id]) ?? null;
      }
      if (bestId) setActiveSection(bestId);
    };

    const { height: viewportHeight } = capabilities.getViewportSize();
    const bandBottomFromViewportTop = REFERENCE_LINE_PX + BAND_HEIGHT_PX;
    const observer = new IntersectionObserver(recompute, {
      rootMargin: `-${REFERENCE_LINE_PX}px 0px -${Math.max(viewportHeight - bandBottomFromViewportTop, 0)}px 0px`,
    });
    for (const id of SECTION_IDS) {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    }
    recompute(); // establish the correct section immediately, not just on the first crossing
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable DOM nodes once mounted; re-observing on every render is unnecessary. viewportHeight is read once per setup (matching this effect's existing lifecycle) rather than tracked live — there is no resize-listener capability port today.
  }, [loadingJob]);

  // job-360 answers only for a job our replica holds, so either read finds it.
  const isFound = job !== null || job360 !== null;
  const stillLoading = loadingJob;

  const scrollToSection = (id: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: true }));
    setActiveSection(id);
    requestAnimationFrame(() => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // Header fields — Tharstern-synced data only. A field neither source has
  // renders as "—", never fabricated.
  const customerName = job?.customer?.name ?? progress?.customer_name ?? null;
  const customerId = job?.customer?.id ?? progress?.customer_id ?? null;
  const productTitle = progress?.title ?? job?.description ?? null;
  const quantity = progress?.quantity ?? job?.quantity ?? null;
  const requiredAt = progress?.required_at ?? job?.due_at ?? null;
  const value = job?.booked_value ?? null;

  // Context for the Timeline section's "Draft reply" / "Draft update to
  // customer" affordances (CSR command-centre Slice 3) — only once there's
  // enough to seed a template with. Never wired up on this page before this
  // redesign; the user asked for it explicitly ("we want to have replies
  // ready to go for the CSRs").
  const replyComposerJob: ReplyComposerJobContext | undefined =
    customerName && productTitle && requiredAt
      ? {
          jobNo,
          jobTitle: productTitle,
          customerName,
          customerId: customerId ?? "",
          requiredAt,
          rag: progress?.rag ?? "green",
        }
      : undefined;

  // The Timeline nav badge and the section's own collapsed hint both need
  // the true entry count under the current filter. JobTimelineSection is
  // the only place that has it — it owns notes/Tharstern notes/documents
  // (fetched internally) and the filter — so it reports back rather than
  // this page recomputing a partial merge from the props it happens to
  // hold (which previously undercounted: a stale "8" against 11 rendered
  // entries).
  const [timelineVisibleCount, setTimelineVisibleCount] = useState<
    number | null
  >(null);
  const handleTimelineVisibleCountChange = useCallback(
    (count: number) => setTimelineVisibleCount(count),
    [],
  );

  if (stillLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading job…</div>
      </div>
    );
  }

  if (!isFound) {
    return (
      <div className="space-y-6">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </button>
        <Card className="p-12 text-center">
          <h3 className="mb-2">Job not found</h3>
          <p className="text-muted-foreground">
            No job or work in progress found for {jobNo}.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-6xl mx-auto">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div>
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate">Job {jobNo}</h1>
            <RagOrDraftPill progress={progress} job={job} />
          </div>
          {customerId ? (
            <Link
              to={paths.customerDetail({ customerId })}
              className="hover:underline text-foreground font-medium shrink-0"
            >
              {customerName}
            </Link>
          ) : (
            <span className="text-foreground font-medium shrink-0">
              {customerName ?? "—"}
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap mt-1">
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
            {productTitle && <span>{productTitle}</span>}
            {quantity != null && (
              <>
                <span>·</span>
                <span>{quantity.toLocaleString()} units</span>
              </>
            )}
            {requiredAt && (
              <>
                <span>·</span>
                <span>Needed by {formatDateOrDash(requiredAt)}</span>
              </>
            )}
          </div>
          {value != null && (
            <span className="text-lg font-semibold tabular-nums shrink-0">
              {formatCurrencyGbp(value, 0)}
            </span>
          )}
        </div>
      </div>

      {/* ── Degraded slices ────────────────────────────────────── */}
      {/* job-360 says explicitly when a slice couldn't be composed — a
          silent empty section would otherwise read as "genuinely nothing
          here" (spec 08 §11 distinguishes those on purpose). */}
      {degradedSlices.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Some information on this page could not be loaded:{" "}
          {degradedSlices
            .map((s) => `${s.slice_name} (${s.reason ?? "unavailable"})`)
            .join(", ")}
        </div>
      )}

      {/* ── Series strip ──────────────────────────────────────────
          Only when this job has real siblings (series_sibling_count > 0
          — NOT series_role === "parent", which is true for ~84% of jobs
          because every standalone job is its own parent_job_no). Renders
          nothing at all on the ~80% of jobs with no series. */}
      {progress && jobNo && (
        <SeriesStrip
          jobNo={jobNo}
          seriesSiblingCount={progress.series_sibling_count ?? 0}
          seriesMembers={progress.series_members ?? []}
        />
      )}

      {/* ── Stage rail ─────────────────────────────────────────── */}
      {progress && (progress.stepper?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="py-3">
            <StageRail
              stepper={progress.stepper!}
              onStopClick={(stageCode) =>
                scrollToSection(
                  STAGE_CODE_TO_SECTION[stageCode] ?? "production",
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {/* ── Why this job needs attention ──────────────────────── */}
      {progress && <AttentionCard progress={progress} jobNo={jobNo} />}

      {/* ── Section nav (narrow) + sections ──────────────────────── */}
      {/* Wraps rather than scrolling sideways, like the stage rail above.
          The chips' intrinsic width is ~630px across 7 sections (more with
          Checks), so below roughly 700px — a phone, or a desktop window at
          high browser zoom — a scrolling strip hides sections off the right
          edge with no affordance saying they are there. Nothing on this page
          scrolls horizontally. */}
      <div className="lg:hidden -mx-1 pb-1">
        <div className="flex flex-wrap gap-1 px-1">
          {SECTION_IDS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => scrollToSection(id)}
              aria-current={activeSection === id ? "true" : undefined}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-sm whitespace-nowrap",
                activeSection === id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {SECTION_LABELS[id]}
              {id === "timeline" &&
              timelineVisibleCount != null &&
              timelineVisibleCount > 0
                ? ` ${timelineVisibleCount}`
                : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
        <nav
          aria-label="On this job"
          className="hidden lg:block sticky top-4 self-start"
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
            On this job
          </div>
          <ul className="space-y-0.5">
            {SECTION_IDS.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(id)}
                  aria-current={activeSection === id ? "true" : undefined}
                  className={cn(
                    "w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-left transition-colors",
                    activeSection === id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <span>{SECTION_LABELS[id]}</span>
                  {id === "timeline" &&
                    timelineVisibleCount != null &&
                    timelineVisibleCount > 0 && (
                      <span className="text-xs tabular-nums">
                        {timelineVisibleCount}
                      </span>
                    )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-0 min-w-0">
          {/* Job detail */}
          <div
            ref={(el) => {
              sectionRefs.current["job-detail"] = el;
            }}
            data-section-id="job-detail"
          >
            <CollapsibleSection
              title={SECTION_LABELS["job-detail"]}
              variant="page-section"
              open={openSections["job-detail"]}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, "job-detail": open }))
              }
            >
              <JobDetailSection
                job={job}
                tharsternJob={tharsternJob}
                tharsternRefsFailed={tharsternRefsFailed}
              />
            </CollapsibleSection>
          </div>

          {/* Order — the Tharstern estimate behind the job, once. */}
          <div
            ref={(el) => {
              sectionRefs.current.order = el;
            }}
            data-section-id="order"
          >
            <CollapsibleSection
              title={SECTION_LABELS.order}
              variant="page-section"
              open={openSections.order}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, order: open }))
              }
            >
              <EstimateSection estimateBrief={estimateBrief} />
            </CollapsibleSection>
          </div>

          {/* Delivery */}
          <div
            ref={(el) => {
              sectionRefs.current.delivery = el;
            }}
            data-section-id="delivery"
          >
            <CollapsibleSection
              title={SECTION_LABELS.delivery}
              variant="page-section"
              open={openSections.delivery}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, delivery: open }))
              }
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    Planned — Tharstern
                  </div>
                  <JobDeliveryBody delivery={jobDelivery} />
                </div>
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    Live tracking — courier
                  </div>
                  <DeliveryPanel
                    tracking={tracking}
                    emptyReason={trackingEmptyReason}
                    untrackedCourier={trackingUntrackedCourier}
                    trackingLoading={trackingLoading}
                    trackingFailed={trackingFailed}
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* Production */}
          <div
            ref={(el) => {
              sectionRefs.current.production = el;
            }}
            data-section-id="production"
          >
            <CollapsibleSection
              title={SECTION_LABELS.production}
              variant="page-section"
              hint={productionSummary(progress)}
              open={openSections.production}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, production: open }))
              }
            >
              {progress && (progress.stages?.length ?? 0) > 0 ? (
                <ProductionTab
                  progress={progress}
                  activeStageKey={activeStageKey}
                  onStageChange={setActiveStageKey}
                />
              ) : (
                <EmptyCard>Nothing recorded on the press floor yet.</EmptyCard>
              )}
            </CollapsibleSection>
          </div>

          {/* Invoice */}
          <div
            ref={(el) => {
              sectionRefs.current.invoice = el;
            }}
            data-section-id="invoice"
          >
            <CollapsibleSection
              title={SECTION_LABELS.invoice}
              variant="page-section"
              hint={invoiceSummary(job, invoiceLine, loadingJob)}
              open={openSections.invoice}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, invoice: open }))
              }
            >
              <InvoiceSection
                job={job}
                invoiceLine={invoiceLine}
                loading={loadingJob}
              />
            </CollapsibleSection>
          </div>

          {/* Timeline */}
          <div
            ref={(el) => {
              sectionRefs.current.timeline = el;
            }}
            data-section-id="timeline"
          >
            <CollapsibleSection
              title={SECTION_LABELS.timeline}
              variant="page-section"
              hint={
                timelineVisibleCount != null && timelineVisibleCount > 0
                  ? `${timelineVisibleCount}`
                  : undefined
              }
              open={openSections.timeline}
              onOpenChange={(open) =>
                setOpenSections((prev) => ({ ...prev, timeline: open }))
              }
            >
              <JobTimelineSection
                jobNo={jobNo}
                customerId={customerId ?? ""}
                emails={emails}
                meetings={meetings}
                commsFailed={commsFailed}
                canUseTharsternAttachments={canUseTharsternAttachments}
                job={replyComposerJob}
                onVisibleCountChange={handleTimelineVisibleCountChange}
              />
            </CollapsibleSection>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attention card — "why does this job need attention", replacing the old
// flat row of identical red pills. `phase_downstream_blocked` is a
// derivative alert (work_visibility_module's
// `exception_detectors/_phase_downstream_blocked.py`): it fires purely
// because some OTHER alert on the job is amber/red, and its own message
// never names that cause. Every other alert (a "cause") is shown in full,
// severity-coloured; every `phase_downstream_blocked` alert is collapsed
// into one trailing "Holding up: <phases>" line instead of repeating
// "blocked by upstream issue" once per phase.
// ---------------------------------------------------------------------------

const PHASE_DOWNSTREAM_BLOCKED_RULE = "phase_downstream_blocked";

// Alert message templates are rendered server-side from
// work_visibility_module's rule seeds (actions/bootstrap.py) and use one of
// two prefix shapes: "Job {job_no} — ..." or "Job {job_no} ...". We're
// already on that job's page, so repeating the job number on every row is
// noise that eats the width the message itself needs. Only a message that
// actually starts with one of these two exact shapes is touched — some
// rules (e.g. "Issue raised against job {job_no}") mention the job number
// mid-sentence and must be left alone.
function stripJobPrefix(message: string, jobNo: string): string {
  const dashPrefix = `Job ${jobNo} — `;
  const spacePrefix = `Job ${jobNo} `;
  if (message.startsWith(dashPrefix)) return message.slice(dashPrefix.length);
  if (message.startsWith(spacePrefix)) return message.slice(spacePrefix.length);
  return message;
}

function titleCaseStageOpType(opType: string): string {
  return opType
    .split("_")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

// The distinct stages named across the collapsed `phase_downstream_blocked`
// alerts, in first-seen order. Matched against `progress.stages` (same
// op_type vocabulary as the stage order server-side) for the human display
// name; falls back to a title-cased op_type when the stage isn't in
// `progress.stages` (e.g. it has no ops yet, so it was never bucketed).
function blockedStageNames(
  blocked: API.WorkAlertSummary[],
  stages: API.JobProgress["stages"],
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const alert of blocked) {
    const opType = alert.stage_op_type;
    if (!opType || seen.has(opType)) continue;
    seen.add(opType);
    const stage = stages?.find((s) => s.op_type === opType);
    names.push(stage?.display_name ?? titleCaseStageOpType(opType));
  }
  return names;
}

function AttentionCard({
  progress,
  jobNo,
}: {
  progress: API.JobProgress;
  jobNo: string;
}) {
  const exceptions = progress.exceptions ?? [];
  const causes = exceptions
    .filter((a) => a.rule_code !== PHASE_DOWNSTREAM_BLOCKED_RULE)
    // Red before amber, then most-recently-detected first.
    .sort((a, b) => {
      if (a.severity !== b.severity) {
        return a.severity === "red" ? -1 : b.severity === "red" ? 1 : 0;
      }
      return (
        new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()
      );
    });
  const blocked = exceptions.filter(
    (a) => a.rule_code === PHASE_DOWNSTREAM_BLOCKED_RULE,
  );
  const hasIssueLink = progress.open_issue_count > 0;

  if (causes.length === 0 && blocked.length === 0 && !hasIssueLink) {
    return null;
  }

  const heldUpStages = blockedStageNames(blocked, progress.stages);

  return (
    <div className="rounded-md border border-border divide-y divide-border overflow-hidden">
      {causes.map((alert) => (
        <div
          key={alert.id}
          className={cn(
            "flex items-start gap-2 px-3 py-2 text-sm",
            alert.severity === "red"
              ? "bg-red-50 text-red-800"
              : alert.severity === "amber"
                ? "bg-amber-50 text-amber-800"
                : "bg-muted text-muted-foreground",
          )}
        >
          <AlertTriangle
            className={cn(
              "size-3.5 mt-0.5 shrink-0",
              alert.severity === "red"
                ? "text-red-500"
                : alert.severity === "amber"
                  ? "text-amber-500"
                  : "text-muted-foreground",
            )}
            aria-hidden
          />
          <div className="min-w-0">
            <span>{stripJobPrefix(alert.message, jobNo)}</span>
            <p className="text-xs opacity-70 mt-0.5">
              Since{" "}
              {formatDistanceToNow(new Date(alert.detected_at), {
                addSuffix: true,
              })}
            </p>
          </div>
        </div>
      ))}
      {blocked.length > 0 && (
        <div className="px-3 py-2 text-sm">
          {/* Plain text, not a bolded "Holding up:" span + sibling text
              node — the two-node split reads fine visually but breaks
              text-matching in tests (RTL matches an element's own direct
              text-node children only, not nested elements' text). */}
          <p className="text-muted-foreground">
            Holding up:{" "}
            {heldUpStages.length > 0
              ? heldUpStages.join(", ")
              : "a downstream stage"}
          </p>
          {/* Edge case: the blocking alerts cleared or were acknowledged
              between detection passes, leaving `blocked` with no `causes`
              to explain them. Say that honestly rather than inventing a
              cause. */}
          {causes.length === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              The upstream issue that was blocking these stages is no longer
              open.
            </p>
          )}
        </div>
      )}
      {hasIssueLink && (
        <Link
          to={
            progress.single_open_issue_id
              ? paths.issueDetail({ id: progress.single_open_issue_id })
              : `${paths.issues()}?customer=${encodeURIComponent(progress.customer_id)}&status=open`
          }
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 transition-colors"
        >
          <AlertCircle className="size-3.5 shrink-0" aria-hidden />
          {progress.open_issue_count} open issue
          {progress.open_issue_count === 1 ? "" : "s"} on this customer
          <ExternalLink className="size-3 ml-auto shrink-0" aria-hidden />
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header pill
// ---------------------------------------------------------------------------

function RagOrDraftPill({
  progress,
  job,
}: {
  progress: API.JobProgress | null;
  job: API.JobViewItem | null;
}) {
  // Once invoiced, the job is done — that outranks a RAG read on the
  // (now moot) production stage. Checked ahead of `progress` so a fully
  // synced, invoiced job shows "Invoiced" rather than "On track".
  if (job?.invoiced_at) {
    return <Badge variant="success">Invoiced</Badge>;
  }
  if (progress) {
    // rag is the alert rollup (derive_rag_from_alerts over active_alerts,
    // see load_job_progress.py), not a deadline signal — "Late" claimed
    // lateness a red alert alone doesn't establish (same conflation fixed
    // on the portfolio board, see PortfolioBoard.tsx's `boardFlag`). The
    // approved trio is "No alerts" / "Warning" / "Overdue" — red is
    // honestly overdue-only now that the one red-severity rule is "Past
    // due date". This pill shows the severity word, not the named alert
    // (`top_alert_label` on WorkUnitSummaryItem/WorkPortfolioItem):
    // `JobProgress` — this page's own progress shape — carries no such
    // field, so there is nothing more specific to show here without a
    // backend change.
    const RAG_LABEL: Record<API.JobProgress["rag"], string> = {
      green: "No alerts",
      amber: "Warning",
      red: "Overdue",
    };
    const RAG_VARIANT: Record<
      API.JobProgress["rag"],
      "success" | "warning" | "error"
    > = { green: "success", amber: "warning", red: "error" };
    return (
      <Badge variant={RAG_VARIANT[progress.rag]}>
        {RAG_LABEL[progress.rag]}
      </Badge>
    );
  }
  return <Badge variant="warning">Work in progress</Badge>;
}

// ---------------------------------------------------------------------------
// Series strip — "a series of jobs" (series_role/series_parent_job_no/
// series_sibling_count are the existing backend vocabulary; this UI uses
// the same word). Links come from the response's own self_url, never
// constructed client-side (frontend-architecture.md's no-client-constructed-
// navigation rule) — mirrors the composed-view self-describing-response
// pattern even though this response isn't served by the view module.
// ---------------------------------------------------------------------------

// Real series run large: the biggest in the LIVE replica is 108 jobs under
// one parent (718493.01), with several over 40. Rendering every member
// would put a wall of ~100 job numbers above the page content — the same
// problem the wrapped stage rail exists to avoid. Show a handful and let
// the rest be asked for.
const SERIES_LINKS_COLLAPSED = 8;

function SeriesStrip({
  jobNo,
  seriesSiblingCount,
  seriesMembers,
}: {
  jobNo: string;
  seriesSiblingCount: number;
  seriesMembers: API.SeriesMember[];
}) {
  const [expanded, setExpanded] = useState(false);

  if (seriesSiblingCount <= 0 || seriesMembers.length === 0) return null;

  const total = seriesMembers.length;
  const position = seriesMembers.findIndex((m) => m.job_no === jobNo);
  const otherMembers = seriesMembers.filter((m) => m.job_no !== jobNo);
  const shown = expanded
    ? otherMembers
    : otherMembers.slice(0, SERIES_LINKS_COLLAPSED);
  const hiddenCount = otherMembers.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
      <span className="font-medium">
        {position >= 0
          ? `Job ${position + 1} of ${total} in this series`
          : `Part of a series of ${total} jobs`}
      </span>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {shown.map((m) => (
          <Link
            key={m.job_no}
            to={m.self_url}
            className="text-[color:var(--color-primary)] hover:underline"
          >
            {m.job_no}
          </Link>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-muted-foreground hover:text-foreground underline"
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage rail
// ---------------------------------------------------------------------------

function StageRail({
  stepper,
  onStopClick,
}: {
  stepper: NonNullable<API.JobProgress["stepper"]>;
  onStopClick: (stageCode: string) => void;
}) {
  return (
    <ol className="flex flex-wrap items-start gap-x-1 gap-y-3">
      {stepper.map((stop, idx) => (
        <li key={stop.stage_code} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onStopClick(stop.stage_code)}
            className="flex flex-col items-center gap-1 px-2 py-1 rounded-md hover:bg-muted transition-colors text-center"
          >
            <span
              className={cn(
                "flex items-center gap-1.5 text-sm font-medium",
                stop.state === "done" && "text-emerald-600",
                stop.state === "in_progress" && "text-primary",
                (stop.state === "upcoming" || stop.state === "skipped") &&
                  "text-muted-foreground",
              )}
            >
              {stop.state === "done" && "✓"}
              {stop.display_name}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {stop.actual_at
                ? formatDateOrDash(stop.actual_at)
                : stop.expected_at
                  ? formatDateOrDash(stop.expected_at)
                  : "—"}
            </span>
          </button>
          {idx < stepper.length - 1 && (
            <span
              aria-hidden
              className="w-6 border-t border-dashed border-border shrink-0"
            />
          )}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Empty placeholder
// ---------------------------------------------------------------------------

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <p className="py-4 text-center text-sm text-muted-foreground">{children}</p>
  );
}

// ---------------------------------------------------------------------------
// Job detail section
// ---------------------------------------------------------------------------

function JobDetailSection({
  job,
  tharsternJob,
  tharsternRefsFailed,
}: {
  job: API.JobViewItem | null;
  tharsternJob: API.TharsternJob | null;
  tharsternRefsFailed: boolean;
}) {
  if (!job) {
    return (
      <EmptyCard>No commercial figures recorded for this job yet.</EmptyCard>
    );
  }
  const margin = job.booked_value
    ? (job.contribution_amt / job.booked_value) * 100
    : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{job.description}</p>

      <div className="grid sm:grid-cols-4 gap-3">
        <QuantityStat label="Ordered" value={tharsternJob?.quantity_ordered} />
        <QuantityStat
          label="Produced"
          value={tharsternJob?.quantity_produced}
        />
        <QuantityStat
          label="Delivered"
          value={tharsternJob?.quantity_delivered}
        />
        <QuantityStat
          label="Invoiced"
          value={job.invoiced_at ? job.quantity : null}
        />
      </div>

      {/* Every row below is `min-w-0` on the flex row (the grid item — a
          CSS grid item's default `min-width: auto` refuses to shrink below
          its content, which is what let a long value spill into the next
          column) plus `min-w-0 truncate` on the `dd` (a flex item needs the
          same override). `truncate` over wrapping: every value here is a
          single-line fact (currency, a percentage, a date, a mono reference,
          a rep name) where a mid-value line-break reads as broken data; the
          `title` keeps the full value one hover away. */}
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">Booked value</dt>
          <dd className="font-medium min-w-0 truncate">
            {formatCurrencyGbp(job.booked_value, 2)}
          </dd>
        </div>
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">Contribution</dt>
          <dd className="font-medium min-w-0 truncate">
            {formatCurrencyGbp(job.contribution_amt, 2)}
          </dd>
        </div>
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">Margin</dt>
          <dd className="font-medium min-w-0 truncate">{margin.toFixed(1)}%</dd>
        </div>
        {job.ordered_at && (
          <div className="flex justify-between gap-2 min-w-0">
            <dt className="text-muted-foreground shrink-0">Booking date</dt>
            <dd className="font-medium min-w-0 truncate">
              {formatDateOrDash(job.ordered_at)}
            </dd>
          </div>
        )}
        {/* Rendered even when blank: a missing customer order reference is
            itself the thing a CSR is chasing. Sourced from
            MainJobDetails.OrderNo — the customer's own PO reference, not our
            sales-order number. */}
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">Customer Order No.</dt>
          {/* Wraps rather than truncating, unlike every other value in this
              list. The PO is the one field here a CSR reads character by
              character to quote back to the customer, and it is free text
              the customer chose, so it can be long and has no meaningful
              prefix — truncating it hides exactly the part being chased and
              puts the whole point of the field behind a hover. */}
          <dd className="font-mono min-w-0 text-right break-words">
            {job.order_no || "—"}
          </dd>
        </div>
        {/* tharstern_jobs.ref1/ref2 — the sales rep / CSR pool codes, read
            live rather than from the replica. "Unavailable" is deliberately
            distinct from "—": a Tharstern outage must not read as "nobody
            assigned". Pooled values ("Group CSR", "Online", "Pollard
            Account") render as-is — they are real values meaning nobody
            individually assigned, not a data gap. */}
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">Sales rep</dt>
          <dd className="font-medium min-w-0 truncate">
            {tharsternRefsFailed ? (
              <span className="text-muted-foreground italic">Unavailable</span>
            ) : (
              tharsternJob?.customer_ref1 || "—"
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">CSR</dt>
          <dd className="font-medium min-w-0 truncate">
            {tharsternRefsFailed ? (
              <span className="text-muted-foreground italic">Unavailable</span>
            ) : (
              tharsternJob?.customer_ref2 || "—"
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function QuantityStat({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  const formatted = value != null ? value.toLocaleString() : "—";
  return (
    <div className="rounded-md border border-border p-2 text-center min-w-0">
      {/* `truncate` over `break-words`: every value here is a single-line
          stat, and breaking a number mid-digit reads as broken data rather
          than a wrapped sentence. The title exposes the full value. */}
      <div
        className="text-lg font-semibold tabular-nums truncate"
        title={formatted}
      >
        {formatted}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estimate section
// ---------------------------------------------------------------------------

const ESTIMATE_STATUS_LABEL: Record<string, string> = {
  open: "Open",
  won: "Won",
  lost: "Lost",
  superseded: "Superseded",
  stale: "Stale",
  tender: "Tender",
};
const ESTIMATE_STATUS_VARIANT: Record<
  string,
  "success" | "warning" | "error" | "neutral" | "secondary"
> = {
  open: "secondary",
  won: "success",
  lost: "error",
  superseded: "neutral",
  stale: "warning",
  tender: "neutral",
};

function EstimateSection({
  estimateBrief,
}: {
  estimateBrief: API.JobEstimateBriefView | null;
}) {
  if (!estimateBrief) {
    return <EmptyCard>No related estimate for this job.</EmptyCard>;
  }
  return (
    <div className="space-y-3">
      {/* `min-w-0` on each cell + `truncate` on the value: a grid item's
          default `min-width: auto` won't shrink below its content, which is
          exactly how `estimate_id` (a 32-char UUID, wrongly rendered here
          before this fix — see `estimate_no` below) collided with the
          Amount cell beside it. Truncate over wrap: single-line stats. */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Estimate No.</div>
          <div
            className="font-medium truncate"
            title={estimateBrief.estimate_no}
          >
            {estimateBrief.estimate_no}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Amount</div>
          <div className="font-medium truncate">
            {formatQuotedPrice(estimateBrief.estimate_amt_pence)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Date</div>
          <div className="font-medium truncate">
            {formatDateOrDash(estimateBrief.estimate_date)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Status</div>
          <Badge
            variant={ESTIMATE_STATUS_VARIANT[estimateBrief.status] ?? "neutral"}
          >
            {ESTIMATE_STATUS_LABEL[estimateBrief.status] ??
              estimateBrief.status}
          </Badge>
        </div>
      </div>
      {/* `_self` read off the response, not constructed — the view-module
          contract (frontend-architecture.md's "no client-constructed
          navigation"). */}
      <Link
        to={estimateBrief._self}
        className="inline-flex items-center gap-1.5 text-sm text-[color:var(--color-primary)] hover:underline"
      >
        <FileText className="w-4 h-4" />
        Open full estimate
        <ExternalLink className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Production section — the live stage stepper + stage panels.
// ---------------------------------------------------------------------------

function productionSummary(
  progress: API.JobProgress | null,
): string | undefined {
  const stages = progress?.stages ?? [];
  if (stages.length === 0) return undefined;
  // Deliberately no operation count here: the stages fixture behind this
  // summary is illustrative, not a verified per-job operation list, and a
  // stated count reads as a fact about the specific job. Say only what's
  // true regardless of which fixture is behind it.
  const allComplete = stages.every((p) => p.state === "complete");
  return allComplete ? "complete" : "in progress";
}

function ProductionTab({
  progress,
  activeStageKey,
  onStageChange,
}: {
  progress: API.JobProgress;
  activeStageKey: string | null;
  onStageChange: (key: string) => void;
}) {
  const stages = progress.stages ?? [];
  const activeStage =
    stages.find((p) => p.op_type === activeStageKey) ?? stages[0] ?? null;
  return (
    <div className="space-y-4">
      <Stepper
        steps={stageSteps(stages)}
        value={activeStageKey ?? stages[0]?.op_type ?? ""}
        onChange={onStageChange}
      />
      {activeStage && <StagePanel stage={activeStage} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice section — the job's own billed line, resolved via the dual
// job_no / order_line_id lookup joined via source_invoice_head_id; see
// docs/features/job-workspace-redesign/02_invoice_linkage_findings.md.
// ---------------------------------------------------------------------------

function invoiceSummary(
  job: API.JobViewItem | null,
  invoiceLine: JobInvoiceLine | null,
  loading: boolean,
): string | undefined {
  if (loading) return undefined;
  if (invoiceLine) {
    // Explicit basis — the timeline entry a few sections up quotes the same
    // line's net figure, and an unlabelled number here previously read as a
    // contradiction (gross here, net there) rather than two views of one
    // line.
    return `${formatQuotedPrice(invoiceLine.net_amount_pence)} net · invoice ${invoiceLine.invoice_no}`;
  }
  if (job?.invoiced_at) return "No invoice record linked";
  return "Not invoiced yet";
}

function InvoiceSection({
  job,
  invoiceLine,
  loading,
}: {
  job: API.JobViewItem | null;
  invoiceLine: JobInvoiceLine | null;
  loading: boolean;
}) {
  if (loading) {
    return <EmptyCard>Loading invoice…</EmptyCard>;
  }
  if (!invoiceLine) {
    return (
      <EmptyCard>
        {job?.invoiced_at
          ? "No invoice record linked. This is expected for a small share of jobs — internal stock-allocation and administrative records flagged invoiced with no customer invoice ever raised."
          : "This job has not been invoiced yet."}
      </EmptyCard>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        This job was billed{" "}
        <span className="font-medium text-foreground">
          {formatQuotedPrice(invoiceLine.net_amount_pence)}
        </span>{" "}
        on invoice{" "}
        <span className="font-medium text-foreground">
          {invoiceLine.invoice_no}
        </span>
        , dated {formatDateOrDash(invoiceLine.invoice_date)}. That invoice may
        also have carried other work — the figures below are this job's own line
        only.
      </p>
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Quantity</div>
          <div className="font-medium truncate">
            {invoiceLine.quantity.toLocaleString()}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Net</div>
          <div className="font-medium truncate">
            {formatQuotedPrice(invoiceLine.net_amount_pence)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">VAT</div>
          <div className="font-medium truncate">
            {formatQuotedPrice(invoiceLine.vat_amount_pence)}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground mb-1">Gross</div>
          <div className="font-medium truncate">
            {formatQuotedPrice(invoiceLine.gross_amount_pence)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {invoiceLine.is_credit_note && (
          <Badge variant="warning">Credit note</Badge>
        )}
        <Badge variant={invoiceLine.posted_to_accounts ? "success" : "neutral"}>
          {invoiceLine.posted_to_accounts
            ? "Posted to accounts"
            : "Not yet posted"}
        </Badge>
      </div>
    </div>
  );
}
